import { isAfter } from 'date-fns';
import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { Column, SortingRule } from 'react-table';

import { BannerPlacement, BannerScope } from '../../../../../typings/enums';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import useBanners from '../../../../hooks/api/banners/useBanners';
import useCreateBanner from '../../../../hooks/api/banners/useCreateBanner';
import useDeactivateBanner from '../../../../hooks/api/banners/useDeactivateBanner';
import useDeleteBanner from '../../../../hooks/api/banners/useDeleteBanner';
import useUpdateBanner from '../../../../hooks/api/banners/useUpdateBanner';
import { getErrorMessage } from '../../../../ReactQuery';
import { icons } from '../../../generic/Icon';
import SimpleReactTable, { MINIMUM_PAGE_SIZE } from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import type { Banner } from '../../../../../shared/types/routes/banners';

import ModalAddEditBanner from './ModalAddEditBanner';
import useAddEditBanner, { bodyContentsJSON } from './useAddEditBanner';
import type { EditModeState, NewBanner } from './useAddEditBanner';

// MAX_BANNERS_COUNT is the number of active and scheduled banners that we allow per organization at any one time.
const MAX_BANNERS_COUNT = 3;

enum BannerStatus {
  Active,
  Scheduled,
  Inactive,
}

type BannerRow = Pick<Banner, 'id' | 'title' | 'startDate' | 'endDate'> & {
  status: BannerStatus;
  statusName: ReactElement;
  // start is the start date displayed in a human readable format.
  start: string;
  // end is the end date displayed in a human readable format, or a dash when an end date is not defined.
  end: string;
  scope: string;
  placement: string;
};

const placementCellLabel: Record<BannerPlacement, string> = {
  [BannerPlacement.All]: 'All',
  [BannerPlacement.Login]: 'Login',
  [BannerPlacement.Dashboard]: 'Dashboard',
};

const getScopeCellLabel = (banner: Banner): string => {
  if (banner.scope.type === BannerScope.Organizations) {
    const count = banner.scopedOrganizations.length;
    return count > 0 ? `Org-Scoped (${count})` : 'Org-Scoped';
  }
  return 'Global';
};

const defaultSortMode: SortingRule = { id: 'status', desc: false };

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

//  Get a date as a locale string, for displaying in the table.
const getBannerDateLocaleString = (date: string) =>
  new Date(date).toLocaleDateString(undefined, dateFormatOptions);

//  Determine if there are less than the max number of banners.
const lessThanMaxRows = (rows: BannerRow[]) =>
  _.sumBy(rows, row => row.status !== BannerStatus.Inactive ? 1 : 0) < MAX_BANNERS_COUNT;

//  Check if a banner is inactive.
const isBannerInactive = (banner: Banner) =>
  banner.isDeactivated || (banner.endDate && isAfter(new Date(), new Date(banner.endDate)));

//  Check if a banner is scheduled.
const isBannerScheduled = (banner: Banner) =>
  isAfter(new Date(banner.startDate), new Date());

//  Get the status of a banner.
const getBannerStatus = (banner: Banner): Pick<BannerRow, 'status' | 'statusName'> => {
  if (isBannerInactive(banner)) {
    return {
      status: BannerStatus.Inactive,
      statusName: (
        <span>Inactive</span>
      ),
    };
  }

  if (isBannerScheduled(banner)) {
    return {
      status: BannerStatus.Scheduled,
      statusName: (
        <span>Scheduled</span>
      ),
    };
  }

  return {
    status: BannerStatus.Active,
    statusName: (
      <span>Active</span>
    ),
  };
};

/**
 * Settings page for notification banners.
 */
const OrganizationBanners = () => {
  const addToast = useAddToast();
  const { onAlert, onConfirm } = useDialogContext();

  //  Fetch banners
  const { data: banners, isLoading: isFetchingBanners } = useBanners();

  //  Create, update, delete, and deactivate banners
  const {
    mutateAsync: createBanner,
    isLoading: isCreatingBanner,
  } = useCreateBanner();
  const {
    mutateAsync: updateBanner,
    isLoading: isUpdatingBanner,
  } = useUpdateBanner();
  const {
    mutateAsync: deleteBanner,
    // isLoading: isDeletingBanner,
  } = useDeleteBanner();
  const {
    mutateAsync: deactivateBanner,
    // isLoading: isDeactivatingBanner,
  } = useDeactivateBanner();

  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode);

  //  Manage the state of the add/edit banner modal.
  const {
    state: addEditState,
    isEdited,
    onAddNewBanner,
    onEditBanner,
    onDuplicateBanner,
    onCloseModal,
    onTitleChange,
    onBodyChange,
    onStartDateChange,
    onEndDateChange,
    onToggleIsDismissible,
    onBannerStyleChange,
    onPlacementChange,
    onScopeChange,
    onScopedOrganizationsChange,
  } = useAddEditBanner();

  //  Create a banner, but close the modal and add a toast when completed.
  //  TODO: have a pattern for this, we do this all over the place.
  const onCreateBanner = async (banner: NewBanner) => {
    try {
      await createBanner({
        ...banner,
        body: bodyContentsJSON(banner),
      });
      onCloseModal();
      addToast({
        type: 'success',
        content: 'Banner created.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Error creating banner.'),
      });
    }
  };

  //  Update a banner, but close the modal and add a toast when completed.
  const onUpdateBanner = async (banner: EditModeState['banner']) => {
    try {
      await updateBanner({
        id: banner.id,
        banner: {
          ...banner,
          body: bodyContentsJSON(banner),
        },
      });
      onCloseModal();
      addToast({
        type: 'success',
        content: 'Banner updated.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Error updating banner.'),
      });
    }
  };

  //  Deactivate a banner, but and add a toast when completed.
  const onDeactivateBanner = async (bannerId: number) => {
    try {
      await deactivateBanner(bannerId);
      addToast({
        type: 'success',
        content: 'Banner deactivated.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Error deactivating banner.'),
      });
    }
  };

  //  Delete a banner, but and add a toast when completed.
  const onDeleteBanner = async (bannerId: number) => {
    try {
      await deleteBanner(bannerId);
      addToast({
        type: 'success',
        content: 'Banner deleted.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Error deleting banner.'),
      });
    }
  };

  const rows: BannerRow[] = useMemo(() => banners?.map((banner) => ({
    id: banner.id,
    title: banner.title,
    ...getBannerStatus(banner),
    startDate: banner.startDate,
    start: getBannerDateLocaleString(banner.startDate),
    // An unspecified end date sorts last: the '~' character is the last character by ASCII value.
    endDate: banner.endDate || '~',
    end: banner.endDate ? getBannerDateLocaleString(banner.endDate) : '–',
    scope: getScopeCellLabel(banner),
    placement: placementCellLabel[banner.placement],
  })) ?? [], [banners]);

  const columns: Array<Column<BannerRow>> = [
    {
      id: 'title',
      Header: (
        <TableHeader title="Title" sort={{ id: 'title', currentMode: sortMode }} />
      ),
      accessor: 'title',
      width: 320,
      resizable: false,
    },
    {
      id: 'status',
      Header: (
        <TableHeader title="Status" sort={{ id: 'status', currentMode: sortMode }} />
      ),
      accessor: 'status',
      resizable: false,
      Cell: ({ original }: { original: BannerRow }) => original.statusName,
    },
    {
      id: 'scope',
      Header: (
        <TableHeader title="Scope" sort={{ id: 'scope', currentMode: sortMode }} />
      ),
      accessor: 'scope',
      resizable: false,
    },
    {
      id: 'placement',
      Header: (
        <TableHeader title="Placement" sort={{ id: 'placement', currentMode: sortMode }} />
      ),
      accessor: 'placement',
      resizable: false,
    },
    {
      id: 'startDate',
      Header: (
        <TableHeader title="Start" sort={{ id: 'startDate', currentMode: sortMode }} />
      ),
      accessor: 'startDate',
      resizable: false,
      Cell: ({ original }: { original: BannerRow }) => original.start,
    },
    {
      id: 'endDate',
      Header: (
        <TableHeader title="End" sort={{ id: 'endDate', currentMode: sortMode }} />
      ),
      accessor: 'endDate',
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: BannerRow }) => <span>{original.end}</span>,
    },
    {
      id: 'actions',
      Header: (
        <TableHeader title="Actions" />
      ),
      accessor: 'actions',
      resizable: false,
      width: 140,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: BannerRow }) => (
        <SettingsTableRowActions>
          <SettingsTableRowActions.IconButton
            icon={icons.pencil}
            onClick={() => onEditBanner(banners?.find(({ id }) => id === original.id) as Banner)}
            tooltipId={`view-banner-${original.id}`}
            tooltip="Edit"
          />
          <SettingsTableRowActions.IconButton
            icon={icons.cloneO}
            onClick={() => {
              if (lessThanMaxRows(rows)) {
                const bannerToDuplicate: Banner | undefined = banners?.find(({ id }) => original.id === id);
                if (bannerToDuplicate) {
                  onDuplicateBanner(bannerToDuplicate);
                }
              } else {
                onAlert({
                  title: 'Banners Limit Reached',
                  body: 'You\'ve reached the maximum number of active and scheduled banners allowed.',
                });
              }
            }}
            tooltipId={`duplicate-banner-${original.id}`}
            tooltip="Duplicate"
          />
          {original.status === BannerStatus.Active && (
            <SettingsTableRowActions.IconButton
              icon={icons.ban}
              onClick={() => onConfirm({
                title: 'Warning',
                body: 'Deactivating a banner will immediately stop displaying it in FOSSA. Are you sure you want to deactivate it now?',
                onConfirm: () => onDeactivateBanner(original.id),
              })}
              tooltipId={`deactivate-banner-${original.id}`}
              tooltip="Deactivate"
            />
          )}
          <SettingsTableRowActions.IconButton
            icon={icons.trashRegular}
            onClick={() => onConfirm({
              title: 'Warning',
              body: 'Deleting a banner is an unrecoverable action. Are you sure you want to delete this banner?',
              onConfirm: () => onDeleteBanner(original.id),
            })}
            tooltipId={`delete-banner-${original.id}`}
            tooltip="Delete"
          />
        </SettingsTableRowActions>
      ),
    },
  ];

  return (
    <>
      <Header heading="Notification Banners">
        <Header.Paragraph>
          Display information to your colleagues while on the sign in and project index pages.
        </Header.Paragraph>
      </Header>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-banner-button"
          size="large"
          type="secondary"
          tooltip="You've reached the maximum number of active and scheduled banners allowed."
          showTooltip={!lessThanMaxRows(rows)}
          isDisabled={!lessThanMaxRows(rows)}
          onClick={onAddNewBanner}
        >
          Add Banner
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.Text>
          Maximum of {MAX_BANNERS_COUNT} active and scheduled banners per managing organization. Selecting target
          organizations on an org-scoped banner does not raise this limit.
        </SettingsPageTableActions.Text>
      </SettingsPageTableActions>
      <SimpleReactTable
        data={rows}
        columns={columns}
        defaultSortMode={[defaultSortMode]}
        onSortedChanged={setSortMode}
        showPagination={rows.length > MINIMUM_PAGE_SIZE}
        cacheId="banners"
        isLoading={isFetchingBanners}
      />
      {addEditState !== null && (
        <ModalAddEditBanner
          state={addEditState}
          isEdited={isEdited}
          isCreatingOrUpdating={isCreatingBanner || isUpdatingBanner}
          onCloseModal={onCloseModal}
          onTitleChange={onTitleChange}
          onBodyChange={onBodyChange}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onToggleIsDismissible={onToggleIsDismissible}
          onBannerStyleChange={onBannerStyleChange}
          onPlacementChange={onPlacementChange}
          onScopeChange={onScopeChange}
          onScopedOrganizationsChange={onScopedOrganizationsChange}
          onSubmitAddEdit={() => {
            if (addEditState?.mode === 'add') {
              onCreateBanner(addEditState.banner);
            } else if (addEditState?.mode === 'edit') {
              onUpdateBanner(addEditState.banner);
            }
          }}
        />
      )}
    </>
  );
};

export default OrganizationBanners;
